import type * as React from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

// Canonical table per DESIGN.md §10.8.
// - wrap: paper bg, 1px --card-line, radius 9px, overflow hidden
// - toolbar: title (13.5px/600) + count (12px muted tabular-nums)
// - header: 10.5px uppercase 600 muted, padding 10px 14px
// - body: 13px ink, padding 12px 14px, min-height 44px, divider --ash-line
// - row hover: sand-step
// - row selected: sand-step + 2px coral left indicator
// - sticky-r: 40px right column for chev/action (shadow on overflow)

export type CanonicalColumn<T> = {
  key: string;
  header: React.ReactNode;
  cell: (item: T) => React.ReactNode;
  /** Right-aligned + tabular-nums (numeric columns). */
  numeric?: boolean;
  /** Extra className for both th and td. */
  className?: string;
  /** Renders the sticky-right action column (40px, shadow on scroll). */
  stickyRight?: boolean;
};

export type CanonicalTableProps<T> = {
  /** Optional toolbar (left side). Title + count are rendered together. */
  title?: React.ReactNode;
  count?: number;
  /** Optional toolbar right-side slot (filters, search, table actions). */
  toolbarRight?: React.ReactNode;

  columns: CanonicalColumn<T>[];
  data: T[];
  getRowId: (item: T) => string;

  isLoading?: boolean;
  /** Empty state body. Rendered inside the table area when data is empty. */
  empty?: React.ReactNode;

  onRowClick?: (item: T) => void;
  isRowSelected?: (item: T) => boolean;

  testIdPrefix?: string;
  className?: string;
};

export function CanonicalTable<T>({
  title,
  count,
  toolbarRight,
  columns,
  data,
  getRowId,
  isLoading,
  empty,
  onRowClick,
  isRowSelected,
  testIdPrefix = "row",
  className,
}: CanonicalTableProps<T>) {
  const hasToolbar = title !== undefined || count !== undefined || toolbarRight !== undefined;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-[9px] border border-[hsl(var(--card-line))] bg-[hsl(var(--paper-white))]",
        className,
      )}
    >
      {hasToolbar && (
        <div className="flex items-center justify-between gap-3 border-b border-[hsl(var(--card-line))] px-3 py-2.5">
          <div className="flex items-baseline gap-2">
            {title !== undefined && (
              <span className="text-[13.5px] font-semibold text-[hsl(var(--ink-primary))]">
                {title}
              </span>
            )}
            {count !== undefined && (
              <span className="text-[12px] tabular-nums text-[hsl(var(--ink-muted))]">{count}</span>
            )}
          </div>
          {toolbarRight && <div className="flex items-center gap-1">{toolbarRight}</div>}
        </div>
      )}

      <div className="relative w-full overflow-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[hsl(var(--card-line))]">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    // Header-Always-Left Rule (DESIGN.md §10.8): header é sempre
                    // text-left, mesmo em coluna .num (apenas a célula é right).
                    "px-3.5 py-2.5 text-left align-middle text-[10.5px] font-semibold uppercase tracking-[0.02em] text-[hsl(var(--ink-muted))]",
                    col.stickyRight && "sticky right-0 w-10 bg-[hsl(var(--paper-white))]",
                    col.className,
                  )}
                  scope="col"
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [0, 1, 2, 3, 4].map((i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton row index is stable for the loading placeholder
                <tr key={i} className="border-b border-[hsl(var(--ash-line))] last:border-b-0">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        "px-3.5 py-3 align-middle text-[13px] text-[hsl(var(--ink-primary))]",
                        col.stickyRight && "sticky right-0 bg-[hsl(var(--paper-white))]",
                        col.className,
                      )}
                      style={{ minHeight: 44 }}
                    >
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3.5 py-10 text-center text-[13px] text-[hsl(var(--ink-muted))]"
                >
                  {empty ?? "Nenhum item para exibir."}
                </td>
              </tr>
            ) : (
              data.map((item) => {
                const id = getRowId(item);
                const selected = isRowSelected?.(item) ?? false;
                const clickable = Boolean(onRowClick);
                return (
                  <tr
                    key={id}
                    onClick={clickable ? () => onRowClick?.(item) : undefined}
                    data-testid={`${testIdPrefix}-${id}`}
                    data-selected={selected || undefined}
                    className={cn(
                      "group relative border-b border-[hsl(var(--ash-line))] last:border-b-0 transition-colors",
                      clickable && "cursor-pointer",
                      "hover:bg-[hsl(var(--sand-step))]",
                      selected &&
                        "bg-[hsl(var(--sand-step))] shadow-[inset_2px_0_0_0_hsl(var(--comp-coral))]",
                    )}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          "px-3.5 py-3 align-middle text-[13px] text-[hsl(var(--ink-primary))] transition-colors",
                          col.numeric && "text-right tabular-nums",
                          col.stickyRight &&
                            "sticky right-0 w-10 bg-[hsl(var(--paper-white))] group-hover:bg-[hsl(var(--sand-step))] shadow-[-8px_0_12px_-10px_rgba(0,0,0,0.18)]",
                          col.stickyRight && selected && "bg-[hsl(var(--sand-step))]",
                          col.className,
                        )}
                        style={{ minHeight: 44 }}
                      >
                        {col.cell(item)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
